import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ARCHETYPE_LABEL, type AccumulationNode, type PortfolioAgent, tierForScore } from "@ti/core";
import { AlertOctagon, AlertTriangle, Boxes, Building2, CalendarClock, Gauge, Landmark, RefreshCcw, Search, ShieldOff, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader, TimeAgo } from "@/components/domain/misc";
import { StatTile } from "@/components/domain/stat";
import { TierBadge } from "@/components/domain/status";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Meter, Skeleton } from "@/components/ui/misc";
import { Tooltip } from "@/components/ui/tooltip";
import { ArsHistogram, TierFrequencyChart } from "@/features/underwriter/charts";
import { useMyRole } from "@/features/accounts/shared";
import { OVERRIDE_META, OverrideDialog, type OverrideKind } from "@/features/underwriter/override-dialog";
import { q } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/underwriter")({
  component: UnderwriterPage,
});

const CONCENTRATION_THRESHOLD = 0.35;
const COMPLETENESS_FLOOR = 0.95;

const DEP_KIND_LABEL: Record<AccumulationNode["kind"], string> = {
  model: "Model",
  mcp_server: "MCP server",
  api: "Third-party API",
  framework: "Framework",
  platform: "Agent platform",
};

function UnderwriterPage() {
  const query = useQuery(q.portfolio());
  const policies = useQuery(q.policies());
  const carriers = [...new Set((policies.data ?? []).filter((x) => x.status === "active").map((x) => x.carrier))];
  const [override, setOverride] = useState<OverrideKind | null>(null);
  const overrides = useQuery(q.underwriterOverrides());
  const records = overrides.data ?? [];
  const canOverride = useMyRole() === "underwriter";
  const p = query.data;

  return (
    <Page>
      <PageHeader
        title="Underwriter portal"
        description="Portfolio risk across insured agents, derived continuously from telemetry. Overrides are recorded with a reason and shown to the policyholder."
        actions={
          carriers.length ? (
            <Badge tone="violet" className="h-7 px-2 text-xs">
              <Landmark /> Underwriter view · {carriers.join(", ")}
            </Badge>
          ) : null
        }
      />

      {query.error ? (
        <ErrorPanel error={query.error} reset={() => void query.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {p ? (
              <>
                <StatTile label="Insured agents" icon={<Building2 />} value={fmtInt(p.insuredAgents)} sub={`${new Set(p.agents.map((a) => a.orgName)).size} customers`} />
                <StatTile label="Total limit in force" icon={<Wallet />} value={fmtMoney(p.totalLimit, { compact: true })} sub={`Avg ${fmtMoney(p.totalLimit.amount / Math.max(1, p.insuredAgents), { compact: true })} per agent`} />
                <StatTile
                  label="Average ARS"
                  icon={<Gauge />}
                  value={
                    <span className="inline-flex items-center gap-2">
                      {p.avgArs}
                      <TierBadge tier={tierForScore(p.avgArs)} />
                    </span>
                  }
                  sub="Exposure-unweighted mean"
                />
                <StatTile label="Loss ratio" icon={<CalendarClock />} value={p.lossRatio === null ? "—" : fmtPct(p.lossRatio)} sub={p.lossRatio === null ? "No claims paid yet" : "Paid + reserved / earned premium"} />
              </>
            ) : (
              Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-[98px]" />)
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="ARS distribution" description="Insured agents per score bucket" />
              <CardBody>{p ? <ArsHistogram data={p.arsHistogram} /> : <Skeleton className="h-[220px]" />}</CardBody>
            </Card>
            <Card>
              <CardHeader title="Incidents per 1k sessions by tier" description="Lower tiers should show higher frequency — the score's actuarial check" />
              <CardBody>{p ? <TierFrequencyChart data={p.tierLossFrequency} /> : <Skeleton className="h-[220px]" />}</CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader
              title="Accumulation risk"
              description={`Insured limit exposed to a single upstream dependency — model, API, MCP server, framework or the agent platform the customer built on. Shares above ${fmtPct(CONCENTRATION_THRESHOLD)} indicate correlated loss concentration — consider an event sub-limit.`}
            />
            <CardBody className="p-0">
              {p ? <PlatformConcentration accumulation={p.accumulation} agents={p.agents} /> : null}
              <AccumulationTable data={p?.accumulation} loading={query.isPending} />
            </CardBody>
          </Card>

          <PortfolioCard agents={p?.agents} loading={query.isPending} />

          <Card>
            <CardHeader
              title="Overrides"
              description={canOverride ? "Manual carrier actions. Each requires a reason, is recorded with your name and is visible to the policyholder." : "Manual carrier actions taken by the program's underwriters, with their reasons."}
            />
            <CardBody className="flex flex-col gap-4">
              {canOverride ? (
              <div className="grid gap-3 md:grid-cols-3">
                {(
                  [
                    ["grace_extension", <CalendarClock key="i" />],
                    ["manual_exclusion", <ShieldOff key="i" />],
                    ["re_rating", <RefreshCcw key="i" />],
                  ] as const
                ).map(([k, icon]) => (
                  <div key={k} className="flex flex-col gap-2 rounded-lg border bg-surface-2/40 p-3">
                    <div className="flex items-center gap-2 text-[13px] font-medium [&_svg]:size-4 [&_svg]:text-fg-3">
                      {icon}
                      {OVERRIDE_META[k].title}
                    </div>
                    <p className="flex-1 text-xs text-fg-3">{OVERRIDE_META[k].description}</p>
                    <Button variant="outline" size="sm" className="self-start" disabled={!p} onClick={() => setOverride(k)}>
                      {OVERRIDE_META[k].cta}…
                    </Button>
                  </div>
                ))}
              </div>
              ) : null}
              {records.length ? (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-3">History</h3>
                  <ul className="flex flex-col divide-y rounded-lg border">
                    {records.map((r) => (
                      <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px]">
                        <Badge tone="neutral">{OVERRIDE_META[r.kind].title}</Badge>
                        <span className="font-mono text-[12px]">{r.agentName}</span>
                        <span className="text-fg-2">{r.detail}</span>
                        <span className="min-w-0 flex-1 truncate text-xs text-fg-3" title={r.reason}>
                          “{r.reason}”
                        </span>
                        <span className="text-xs text-fg-3">{r.actor}</span>
                        <TimeAgo iso={r.at} className="text-xs" />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : !canOverride ? (
                <p className="text-xs text-fg-3">No overrides yet.</p>
              ) : null}
            </CardBody>
          </Card>
        </div>
      )}

      <OverrideDialog
        kind={override}
        onOpenChange={(o) => !o && setOverride(null)}
      />
    </Page>
  );
}

/** Platform concentration: one agent platform's bad release, outage or exploit hits every customer that built on it. */
function PlatformConcentration({ accumulation, agents }: { accumulation: AccumulationNode[]; agents: PortfolioAgent[] }) {
  const platforms = accumulation.filter((d) => d.kind === "platform").sort((a, b) => b.share - a.share);
  const top = platforms[0];
  if (!top) return null;
  const customers = (name: string) => new Set(agents.filter((a) => a.platform === name).map((a) => a.orgName)).size;
  const total = agents.reduce((s, a) => s + a.limit.amount, 0);
  const onPlatforms = agents.filter((a) => a.platform).reduce((s, a) => s + a.limit.amount, 0);
  const hot = top.share > CONCENTRATION_THRESHOLD;
  return (
    <div className="flex flex-wrap items-start gap-3 border-b bg-surface-2/40 px-4 py-3">
      <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md border bg-surface text-fg-3">
        <Boxes className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-fg">
          Platform concentration
          {hot ? (
            <Badge tone="warning">
              <AlertTriangle /> {top.dependency} above {fmtPct(CONCENTRATION_THRESHOLD)}
            </Badge>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-fg-3">
          {total ? `${fmtPct(onPlatforms / total)} of portfolio limit runs on agent platforms. ` : null}
          A single platform release, outage or exploit lands on every customer that built on it at once.
        </p>
        <dl className="mt-2 flex flex-wrap gap-x-7 gap-y-2">
          {platforms.map((d) => (
            <div key={d.dependency}>
              <dt className="text-[11px] text-fg-3">{d.dependency}</dt>
              <dd className="text-[13px] tabular text-fg">
                <span className="font-semibold">{fmtPct(d.share)}</span>
                <span className="text-fg-3">
                  {" "}
                  · {fmtMoney(d.limitExposed, { compact: true })} · {fmtInt(d.insuredAgents)} agents · {fmtInt(customers(d.dependency))} customers
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function AccumulationTable({ data, loading }: { data: AccumulationNode[] | undefined; loading: boolean }) {
  const columns = useMemo<ColumnDef<AccumulationNode>[]>(
    () => [
      {
        id: "dependency",
        header: "Dependency",
        accessorKey: "dependency",
        cell: ({ row: { original: d } }) =>
          d.kind === "platform" ? <span className="font-medium text-fg">{d.dependency}</span> : <span className="font-mono text-[12px]">{d.dependency}</span>,
      },
      {
        id: "kind",
        header: "Kind",
        accessorKey: "kind",
        cell: ({ row: { original: d } }) =>
          d.kind === "platform" ? (
            <Badge tone="neutral">
              <Boxes /> {DEP_KIND_LABEL[d.kind]}
            </Badge>
          ) : (
            <span className="text-fg-2">{DEP_KIND_LABEL[d.kind]}</span>
          ),
      },
      { id: "agents", header: "Insured agents", accessorKey: "insuredAgents", meta: { align: "right" } },
      {
        id: "limit",
        header: "Limit exposed",
        accessorFn: (d) => d.limitExposed.amount,
        meta: { align: "right" },
        cell: ({ row: { original: d } }) => fmtMoney(d.limitExposed, { compact: true }),
      },
      {
        id: "share",
        header: "Share of total limit",
        accessorKey: "share",
        meta: { width: "38%" },
        cell: ({ row: { original: d } }) => {
          const hot = d.share > CONCENTRATION_THRESHOLD;
          return (
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Meter value={d.share} label={`${d.dependency} share of total limit`} tone={hot ? "var(--warning)" : "var(--series-1)"} className="h-2" />
                <span aria-hidden className="absolute inset-y-[-3px] w-px bg-fg-3" style={{ left: `${CONCENTRATION_THRESHOLD * 100}%` }} title={`${fmtPct(CONCENTRATION_THRESHOLD)} threshold`} />
              </div>
              <span className="w-10 text-right text-xs font-medium tabular">{fmtPct(d.share)}</span>
              <span className="w-[104px]">
                {hot ? (
                  <Tooltip content="Correlated loss concentration: one upstream incident could trigger losses across this share of the portfolio.">
                    <Badge tone="warning" tabIndex={0}>
                      <AlertTriangle /> Concentration
                    </Badge>
                  </Tooltip>
                ) : null}
              </span>
            </div>
          );
        },
      },
    ],
    [],
  );
  return <DataTable data={data} columns={columns} loading={loading} dense initialSorting={[{ id: "share", desc: true }]} stickyHeader={false} empty={{ title: "No shared dependencies" }} />;
}

function PortfolioCard({ agents, loading }: { agents: PortfolioAgent[] | undefined; loading: boolean }) {
  const [search, setSearch] = useState("");
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? agents?.filter((a) => `${a.orgName} ${a.platform ?? "in-house"} ${a.agentName} ${ARCHETYPE_LABEL[a.archetype]}`.toLowerCase().includes(s)) : agents;
  }, [agents, search]);

  const columns = useMemo<ColumnDef<PortfolioAgent>[]>(
    () => [
      { id: "org", header: "Customer", accessorKey: "orgName", cell: ({ row: { original: a } }) => <span className="whitespace-nowrap">{a.orgName}</span> },
      { id: "agent", header: "Agent", accessorKey: "agentName", cell: ({ row: { original: a } }) => <span className="whitespace-nowrap font-mono text-[12px]">{a.agentName}</span> },
      {
        id: "platform",
        header: "Platform",
        accessorFn: (a) => a.platform ?? "",
        cell: ({ row: { original: a } }) => (a.platform ? <span className="whitespace-nowrap text-fg-2">{a.platform}</span> : <span className="text-fg-3">In-house</span>),
      },
      { id: "archetype", header: "Archetype", accessorFn: (a) => ARCHETYPE_LABEL[a.archetype], cell: ({ getValue }) => <span className="text-fg-2">{getValue<string>()}</span> },
      { id: "tier", header: "Tier", accessorKey: "tier", cell: ({ row: { original: a } }) => <TierBadge tier={a.tier} showLabel={false} /> },
      { id: "ars", header: "ARS", accessorKey: "ars", meta: { align: "right" } },
      { id: "exposure", header: "Exposure 30d", accessorFn: (a) => a.exposure30d.amount, meta: { align: "right" }, cell: ({ row: { original: a } }) => fmtMoney(a.exposure30d, { compact: true }) },
      { id: "limit", header: "Limit", accessorFn: (a) => a.limit.amount, meta: { align: "right" }, cell: ({ row: { original: a } }) => fmtMoney(a.limit, { compact: true }) },
      {
        id: "critical",
        header: "Open critical",
        accessorKey: "openCritical",
        meta: { align: "right" },
        cell: ({ row: { original: a } }) =>
          a.openCritical ? (
            <Badge tone="critical" className="tabular">
              <AlertOctagon /> {a.openCritical} critical
            </Badge>
          ) : (
            <span className="text-fg-3">0</span>
          ),
      },
      {
        id: "completeness",
        header: "Completeness",
        accessorKey: "completeness",
        meta: { align: "right" },
        cell: ({ row: { original: a } }) =>
          a.completeness < COMPLETENESS_FLOOR ? (
            <Tooltip content={`Below the ${fmtPct(COMPLETENESS_FLOOR)} telemetry floor — coverage may be suspended`}>
              <span tabIndex={0} className="inline-flex items-center gap-1 text-warning-text">
                <AlertTriangle className="size-3" aria-label="Below floor" />
                {fmtPct(a.completeness, 1)}
              </span>
            </Tooltip>
          ) : (
            fmtPct(a.completeness, 1)
          ),
      },
      { id: "autonomy", header: "Autonomy", accessorKey: "autonomy", meta: { align: "center" }, cell: ({ row: { original: a } }) => <span className="font-mono text-[12px] text-fg-2">{a.autonomy}</span> },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader
        title="Insured portfolio"
        description={agents ? `${rows?.length ?? 0} of ${agents.length} agents` : "Loading…"}
        actions={
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" aria-hidden />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by customer, platform, agent" aria-label="Filter portfolio" className="w-64 pl-7" />
          </div>
        }
      />
      <CardBody className="p-0">
        <DataTable
          data={rows}
          columns={columns}
          loading={loading}
          dense
          className="max-h-[560px]"
          initialSorting={[{ id: "limit", desc: true }]}
          getRowId={(a) => `${a.orgName}/${a.agentName}`}
          empty={{ title: "No agents match", description: "Try a different filter." }}
        />
      </CardBody>
    </Card>
  );
}
